import { QueryFunctionContext, queryOptions } from "@tanstack/react-query";
import { fetchWrapper, TENDER_PACKAGE_SERVICE_URL } from "../index";
import {
  BasicInfoBodyRequest,
  PublicGetTenderPackageById,
  TenderPackage,
  type UpdateContract,
} from "./types";
import { isFeatureFlagOn } from "@/service/featureFlags.ts";
import {
  Client,
  refreshToken,
  setOrgHeader,
  setWorkspaceHeader,
} from "../client.ts";
import { getWorkspace } from "@/context/index.ts";
import { createApiClient, type ApiTransport } from "@supply25/http/client";
import { contractsApi } from "@supply25/shared/tenderPackage/contractsApi";
import { packagesApi } from "@supply25/shared/tenderPackage/packagesApi";
import { supplyChainApi } from "@supply25/shared/tenderPackage/supplyChainApi";
import type { InferInput } from "valibot";

const client = new Client(TENDER_PACKAGE_SERVICE_URL, {
  before: [refreshToken(), setOrgHeader, setWorkspaceHeader],
  credentials: "include",
  headers: { "content-type": "application/json" },
});

const publicClient = new Client(TENDER_PACKAGE_SERVICE_URL, {
  before: [],
  headers: { "content-type": "application/json" },
});

// TODO: drop the /v2 prefix at cutover, when the rewritten fat lambda is done
const v2Transport = new Client(`${TENDER_PACKAGE_SERVICE_URL}/v2`, {
  before: [refreshToken(), setOrgHeader, setWorkspaceHeader],
  credentials: "include",
  headers: { "content-type": "application/json" },
});

const v2Send: ApiTransport = (request) =>
  v2Transport.send(request.method, `/${request.path.replace(/^\//, "")}`, {
    ...(request.body && typeof request.body === "object"
      ? { json: request.body }
      : {}),
  });

/**
 * Typed clients for the api schemas: call signatures come from each
 * schema's input, responses are runtime-parsed with its output.
 */
export const contracts = createApiClient(contractsApi, v2Send);
export const packages = createApiClient(packagesApi, v2Send);

// The supply chain lambda has no /v2 prefix
const rootSend: ApiTransport = (request) =>
  client.send(request.method, `/${request.path.replace(/^\//, "")}`, {
    ...(request.body && typeof request.body === "object"
      ? { json: request.body }
      : {}),
  });
export const supplyChain = createApiClient(supplyChainApi, rootSend);

export const postTenderPackageBasicInfo = async (
  body: BasicInfoBodyRequest,
  organisationId: string,
  workspaceId?: string
): Promise<{ data: [TenderPackage] }> => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId);
  }

  const res = await fetchWrapper(TENDER_PACKAGE_SERVICE_URL, {
    headers: headers,
    method: "POST",
    body: JSON.stringify(body),
    credentials: "include",
  });

  return res;
};

export const postPackageBasicInfo = (json: BasicInfoBodyRequest) => {
  return client.post("/", { json }) as Promise<{ data: [TenderPackage] }>;
};

export const getTenderPackages = async (): Promise<{
  data: TenderPackage[];
}> => {
  return client.get("/") as Promise<{ data: TenderPackage[] }>;
};

interface DeleteTenderPackage {
  tenderId: string;
  organisationId: string;
  workspaceId?: string;
}
export const deleteTenderPackage = async (param: DeleteTenderPackage) => {
  const { tenderId, organisationId, workspaceId } = param;
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId as string);
  }

  const res = await fetchWrapper(`${TENDER_PACKAGE_SERVICE_URL}/${tenderId}`, {
    headers: headers,
    method: "DELETE",
    credentials: "include",
  });

  return await res;
};

export const deletePackage = (packageId: string) => {
  return client.delete(`/${packageId}`);
};

type GetPackageCtx = QueryFunctionContext<
  [string, string, "getPackage", string]
>;

export const getTenderPackage = async ({ queryKey }: GetPackageCtx) => {
  const [orgId, workspaceId, , packageId] = queryKey;
  const { data } = await (client.get(`/${packageId}`, {
    headers: { "organisation-id": orgId, "workspace-id": workspaceId },
  }) as Promise<{ data: [TenderPackage] }>);
  return data[0];
};

export const getTenderPackageById = async ({
  queryKey,
}: QueryFunctionContext): Promise<{ data: [TenderPackage] }> => {
  const [tenderId, organisationId, workspaceId] = queryKey;

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId as string);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId as string);
  }

  const res = await fetchWrapper(`${TENDER_PACKAGE_SERVICE_URL}/${tenderId}`, {
    headers: headers,
    method: "GET",
    credentials: "include",
  });

  return res;
};

export const updateTenderPackage = async (
  tenderId: string,
  body: BasicInfoBodyRequest,
  organisationId: string,
  workspaceId?: string
) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId);
  }

  const res = await fetchWrapper(`${TENDER_PACKAGE_SERVICE_URL}/${tenderId}`, {
    headers: headers,
    method: "PUT",
    body: JSON.stringify(body),
    credentials: "include",
  });

  return res;
};

export const updatePackage = (
  packageId: string,
  json: BasicInfoBodyRequest
) => {
  return client.put(`/${packageId}`, { json });
};

export const updatePackageSubmissionDeadline = (
  packageId: string,
  submissionDeadline?: string
) => {
  return client.put(`/${packageId}/submission-deadline`, {
    json: { submissionDeadline },
  });
};

interface ActivateTenderPackage {
  tenderId: string;
  organisationId: string;
  workspaceId?: string;
}

export const activateTenderPackage = async ({
  tenderId,
  organisationId,
  workspaceId,
}: ActivateTenderPackage) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);

  if (isFeatureFlagOn("workspace") && workspaceId) {
    headers.append("Workspace-Id", workspaceId);
  }

  const res = await fetchWrapper(
    `${TENDER_PACKAGE_SERVICE_URL}/${tenderId}/activate`,
    {
      headers: headers,
      method: "PUT",
      credentials: "include",
    }
  );

  return res;
};

export const publicGetPackageQuery = (collectionId: string) =>
  queryOptions({
    queryKey: ["packages", collectionId],
    queryFn: () =>
      publicClient.get(`/public/${collectionId}`) as Promise<{
        data: PublicGetTenderPackageById[];
      }>,
    select: (res) => res.data[0],
  });

interface MarkTenderPackageViewed {
  organisationId: string;
  workspaceId: string;
  buyerOrganisationId: string;
  buyerWorkspaceId: string;
  tenderPackageId: string;
}

export const markTenderPackageViewed = async ({
  organisationId,
  workspaceId,
  tenderPackageId,
  buyerOrganisationId,
  buyerWorkspaceId,
}: MarkTenderPackageViewed) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId);
  headers.append("Workspace-Id", workspaceId);

  const res = await fetchWrapper(`${TENDER_PACKAGE_SERVICE_URL}/viewed`, {
    headers: headers,
    method: "POST",
    credentials: "include",
    body: JSON.stringify({
      tenderPackageId,
      buyerOrganisationId,
      buyerWorkspaceId,
    }),
  });

  return res;
};

export const getPackageViews = ({
  queryKey,
}: QueryFunctionContext<[string, string, "views"]>) => {
  const [orgId, tenderPackageId] = queryKey;
  return client.get(`/viewed/${tenderPackageId}`, {
    headers: { "Organisation-Id": orgId },
  }) as Promise<{ viewCount: number }>;
};

export const listMyViewedTenderPackages = async ({
  queryKey,
}: QueryFunctionContext): Promise<{ data: TenderPackage[] }> => {
  const [organisationId, workspaceId] = queryKey;

  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Organisation-Id", organisationId as string);
  headers.append("Workspace-Id", workspaceId as string);

  const res = await fetchWrapper(`${TENDER_PACKAGE_SERVICE_URL}/viewed`, {
    headers: headers,
    method: "GET",
    credentials: "include",
  });

  return res;
};

export const createContract = async (
  body: InferInput<typeof contractsApi.create.input>
) => {
  return contracts.create({ body });
};

export const transferContract = (
  body: InferInput<typeof contractsApi.transfer.input>
) => {
  return contracts.transfer({ body });
};

export const awardPackage = (
  body: InferInput<typeof packagesApi.award.input>
) => {
  return packages.award({ body });
};

export const unawardPackage = (assessmentPackageId: string) => {
  return packages.unaward({ body: { assessmentPackageId } });
};

export const listContractsQuery = () => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, "contract"],
    queryFn: () => contracts.list(),
    select: ({ data }) => data,
  });
};

export const getContractQuery = (contractId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, contractId, "getContract"],
    queryFn: () => contracts.get({ params: { contractId } }),
    select: ({ data }) => data,
  });
};

export const listContractMembersQuery = (contractId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, contractId, "contractMembers"],
    queryFn: () => contracts.listMembers({ body: { contractId } }),
    select: ({ data }) => data,
  });
};

export const addContractMember = (
  body: InferInput<typeof contractsApi.addMember.input>
) => {
  return contracts.addMember({ body });
};

export const removeContractMember = (
  body: InferInput<typeof contractsApi.removeMember.input>
) => {
  return contracts.removeMember({ body });
};

export const updateContract = (contractId: string, json: UpdateContract) => {
  return contracts.update({ body: { contractId, ...json } });
};

const selectSupplyChain = ({
  data,
}: Awaited<ReturnType<typeof supplyChain.getWorkspace>>) => data;

export const getSupplyChainQuery = () => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, "supplyChain"],
    queryFn: () => supplyChain.getWorkspace(),
    select: selectSupplyChain,
  });
};

const selectOrganisationSupplyChain = ({
  data,
}: Awaited<ReturnType<typeof supplyChain.getOrganisation>>) => data;

export const getOrganisationSupplyChainQuery = () => {
  const { organisation } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, "supplyChain", "organisation"],
    queryFn: () => supplyChain.getOrganisation(),
    select: selectOrganisationSupplyChain,
  });
};
